/**
 * SSE Service — Phase 4 Real-Time
 * ================================
 * Singleton de connexion SSE vers le stream Oracle Sigma.
 *
 * Usage dans Layout.jsx :
 *
 *   import { connectSSE, disconnectSSE } from '@/services/sseService';
 *
 *   useEffect(() => {
 *     connectSSE({
 *       onSigmaSealed: (payload) => {
 *         useNexusStore.getState().sealDecision(payload.decisionId, payload.sealHash);
 *       },
 *       onStatusChange: (status) => {
 *         useNexusStore.getState().setSSEStatus(status);
 *       },
 *     });
 *     return () => disconnectSSE();
 *   }, []);
 */

export type SSEStatus = 'CONNECTING' | 'LIVE' | 'RECONNECTING' | 'OFFLINE';

export interface SigmaSealedPayload {
  decisionId: string;
  sealHash: string;
  oracleConfirmation: boolean;
  timestamp: number;
}

export interface SSECallbacks {
  onSigmaSealed?: (payload: SigmaSealedPayload) => void;
  onStatusChange?: (status: SSEStatus) => void;
  onError?: (error: Event) => void;
}

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

// PHASE 4 FIX: aligné sur le vrai endpoint SSE du backend Flask
// streams/sse_sigma.py → route /api/sigma/stream
const SSE_ENDPOINT =
  (typeof process !== 'undefined' && process.env?.NEXT_PUBLIC_ORACLE_SSE_URL) ||
  (typeof window !== 'undefined' &&
    window.__ORACLE_SSE_URL__) ||
  `${typeof window !== 'undefined' ? window.location.origin : 'http://localhost:5000'}/api/sigma/stream`;

const RECONNECT_DELAY_MS = 3_000;   // 3s avant retry
const MAX_RECONNECT_ATTEMPTS = 10;  // abandon après 10 échecs consécutifs

// ---------------------------------------------------------------------------
// Singleton state
// ---------------------------------------------------------------------------

let _eventSource: EventSource | null = null;
let _callbacks: SSECallbacks = {};
let _reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let _reconnectAttempts = 0;
let _currentStatus: SSEStatus = 'OFFLINE';

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function _setStatus(status: SSEStatus): void {
  if (_currentStatus === status) return;
  _currentStatus = status;
  _callbacks.onStatusChange?.(status);
  console.info(`[SSE-Service] Status → ${status}`);
}

function _cleanup(): void {
  if (_reconnectTimer) {
    clearTimeout(_reconnectTimer);
    _reconnectTimer = null;
  }
  if (_eventSource) {
    _eventSource.close();
    _eventSource = null;
  }
}

function _connect(): void {
  _cleanup();
  _setStatus('CONNECTING');

  console.info(`[SSE-Service] Connexion → ${SSE_ENDPOINT}`);
  const es = new EventSource(SSE_ENDPOINT);
  _eventSource = es;

  // Confirmation de connexion initiale
  es.addEventListener('connected', () => {
    _reconnectAttempts = 0;
    _setStatus('LIVE');
    console.info('[SSE-Service] Stream LIVE ✓');
  });

  // Heartbeat keepalive — réinitialise le compteur d'erreurs
  es.addEventListener('heartbeat', () => {
    if (_currentStatus !== 'LIVE') {
      _reconnectAttempts = 0;
      _setStatus('LIVE');
    }
  });

  // Événement Sigma Seal — c'est ici que les cartes passent en VERT
  es.addEventListener('sigma_sealed', (event: MessageEvent) => {
    try {
      const payload: SigmaSealedPayload = JSON.parse(event.data);
      console.debug('[SSE-Service] sigma_sealed ←', payload);
      _callbacks.onSigmaSealed?.(payload);
    } catch (err) {
      console.error('[SSE-Service] Erreur parsing sigma_sealed:', err);
    }
  });

  // Événement générique message (fallback pour backends qui n'utilisent pas de named events)
  es.onmessage = (event: MessageEvent) => {
    try {
      const data = JSON.parse(event.data);
      // Si le backend envoie un sigma_sealed via onmessage au lieu d'un named event
      if (data?.event === 'sigma_sealed' || (data?.decisionId && data?.sealHash)) {
        console.debug('[SSE-Service] sigma_sealed via onmessage ←', data);
        _callbacks.onSigmaSealed?.(data as SigmaSealedPayload);
      }
    } catch {
      // Message non-JSON ignoré (ex: heartbeat texte)
    }
  };

  // Erreur / déconnexion → reconnexion automatique
  es.onerror = (error: Event) => {
    _callbacks.onError?.(error);
    console.warn(
      `[SSE-Service] Erreur (attempt ${_reconnectAttempts + 1}/${MAX_RECONNECT_ATTEMPTS})`
    );

    if (_reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
      console.error('[SSE-Service] Max reconnect atteint — abandon');
      _setStatus('OFFLINE');
      _cleanup();
      return;
    }

    _setStatus('RECONNECTING');
    _reconnectAttempts += 1;
    _cleanup();

    // Backoff exponentiel plafonné à 9s
    _reconnectTimer = setTimeout(() => {
      _connect();
    }, RECONNECT_DELAY_MS * Math.min(_reconnectAttempts, 3));
  };
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Initialise la connexion SSE.
 * À appeler dans useEffect(() => { connectSSE({...}); return disconnectSSE; }, [])
 */
export function connectSSE(callbacks: SSECallbacks = {}): void {
  _callbacks = callbacks;
  _reconnectAttempts = 0;
  _connect();
}

/**
 * Ferme la connexion SSE proprement.
 * À retourner comme cleanup dans useEffect.
 */
export function disconnectSSE(): void {
  _cleanup();
  _setStatus('OFFLINE');
  console.info('[SSE-Service] Déconnecté');
}

/**
 * Retourne le statut courant de la connexion SSE.
 */
export function getSSEStatus(): SSEStatus {
  return _currentStatus;
}

// Type augmentation pour window.__ORACLE_SSE_URL__ (optionnel, config runtime)
declare global {
  interface Window {
    __ORACLE_SSE_URL__?: string;
  }
}
